import { useEffect, useMemo, useState, type FC } from "react";

import { type OrganizationManagerNotification } from "@entities";
import dayjs from "dayjs";
import {
  GenericTabs,
  type TabType,
} from "../../components/generic-tabs/GenericTabs";
import { useNotifications } from "../../contexts/NotificationProvider";
import "./Notifications.scss";
import { OneNotification } from "./OneNotification";

export const Notifications: FC = () => {
  const { notifications, markAllAsRead } = useNotifications();
  const [chosenTab, setChosenTab] = useState<"all" | "not-read">(() =>
    notifications.every((v) => v.hasRead) ? "all" : "not-read",
  );

  useEffect(() => {
    return () => markAllAsRead();
  }, []);

  const unreadNotifications = useMemo(
    () => notifications.filter((v) => !v.hasRead),
    [notifications],
  );

  const groupedNotifications = useMemo(() => {
    const today = dayjs();
    const yesterday = dayjs().subtract(1, "day");

    const result: {
      today: OrganizationManagerNotification[];
      yesterday: OrganizationManagerNotification[];
      rest: OrganizationManagerNotification[];
    } = {
      today: [],
      yesterday: [],
      rest: [],
    };

    (chosenTab === "not-read" ? unreadNotifications : notifications).forEach(
      (notification) => {
        const itemDate = dayjs(notification.createdAt);
        if (itemDate.isSame(today, "day")) {
          result.today.push(notification);
        } else if (itemDate.isSame(yesterday, "day")) {
          result.yesterday.push(notification);
        } else {
          result.rest.push(notification);
        }
      },
    );

    return [
      { date: "היום", items: result.today },
      { date: "אתמול", items: result.yesterday },
      { date: "מוקדם יותר", items: result.rest },
    ];
  }, [unreadNotifications, chosenTab]);

  const tabs = useMemo<TabType[]>(
    () => [
      {
        onClick: () => {
          setChosenTab("not-read");
        },
        text: "לא נקרא",
        spanText: String(unreadNotifications.length || ""),
        value: "not-read",
      },
      {
        onClick: () => {
          setChosenTab("all");
        },
        text: "הכל",
        value: "all",
      },
    ],
    [unreadNotifications.length],
  );

  const notificationsLength =
    chosenTab === "all" ? notifications.length : unreadNotifications.length;

  return (
    <div className="desktop-notifications-container">
      <h1 className="title">התראות</h1>
      <GenericTabs tabs={tabs} chosenTab={chosenTab} />
      {notificationsLength === 0 ? (
        <p>אין התראות במערכת</p>
      ) : (
        <div className="notifications-grouped-container">
          {groupedNotifications?.map((group) => {
            return (
              <div key={group.date} className="notification-big-container">
                {group.items.length > 0 && (
                  <>
                    <p className="notification-group">{group.date}</p>
                    {group.items.map((notification) => {
                      return (
                        <OneNotification
                          key={notification.id}
                          notification={notification}
                        />
                      );
                    })}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
